import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CONSENT_STORAGE_KEY, getStoredConsent, initClarity, setStoredConsent } from "@/lib/clarity";

/**
 * Lightweight cookie banner. Shown until the visitor accepts or declines.
 *
 * - Accept → stores "granted" and immediately initializes Microsoft Clarity.
 * - Decline → stores "denied"; no analytics tags are loaded this session
 *   or any future session until the visitor changes the choice on
 *   /cookie-policy.
 *
 * No PII is collected, no advertising tags. See CookiePolicy.tsx.
 *
 * Lazy useState initializer (vs the legacy useState(true) + useEffect flip)
 * makes the first paint already correct — no "hidden → shown" transient
 * that could race with mount/unmount cycles. State only flips via explicit
 * user action (accept/decline) or a cross-tab storage event.
 */
export function CookieConsent() {
  // Lazy initializer — runs once on mount, synchronously. Banner is visible
  // iff there is no stored consent. SSR-safe (window check) even though this
  // app is Vite-SPA today; cheap insurance for future SSR.
  const [show, setShow] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return getStoredConsent() === null;
  });

  useEffect(() => {
    if (getStoredConsent() === "granted") initClarity();

    // React to choices made elsewhere (e.g. the cookie policy page in another tab).
    const onStorage = (e: StorageEvent) => {
      if (e.key === CONSENT_STORAGE_KEY) {
        setShow(getStoredConsent() === null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!show) return null;

  const accept = () => {
    setStoredConsent("granted");
    initClarity();
    setShow(false);
  };
  const decline = () => {
    setStoredConsent("denied");
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie preferences"
      className="cookie-consent fixed inset-x-3 bottom-3 z-[90] md:inset-x-auto md:right-4 md:bottom-4 md:max-w-md"
    >
      <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-4 md:p-5">
        <p className="text-sm font-semibold text-slate-900">We use cookies for product analytics</p>
        <p className="mt-1 text-xs text-slate-600 leading-relaxed">
          We use Microsoft Clarity (and basic Google Analytics, if enabled) to
          understand how the product is used so we can improve it. No personally
          identifiable information is collected and we never share data with
          advertisers.{" "}
          <Link to="/cookie-policy" className="underline font-medium text-slate-700 hover:text-slate-900">
            Learn more
          </Link>
          .
        </p>
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={decline} className="text-slate-600">
            Decline
          </Button>
          <Button size="sm" onClick={accept} className="bg-slate-900 text-white hover:bg-slate-800">
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
